import React from 'react'
import { Paper, Typography, Button, Stack, Box } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import VimeoEmbed from './VimeoEmbed'

interface TutorialVideoProps {
  videoSrc: string
  title?: string
}

const TutorialVideo: React.FC<TutorialVideoProps> = ({ videoSrc, title = 'Tutorial Video' }) => {
  return (
    <Box
      sx={{
        width: '100%',
        background: '#000',
        borderRadius: '8px',
        overflow: 'hidden',
        lineHeight: 0,
      }}
    >
      <video
        src={videoSrc}
        title={title}
        width="100%"
        height="auto"
        controls
        controlsList="nodownload nofullscreen"
        disablePictureInPicture
        muted
        autoPlay
        playsInline
      >
        Your browser does not support the video tag.
      </video>
    </Box>
  )
}

interface WelcomeScreenProps {
  title: React.ReactNode
  content: React.ReactNode
  videoSrc?: string
  videoType?: 'mp4' | 'vimeo'
  onStart: () => void
  onExit: () => void
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  title,
  content,
  videoSrc,
  videoType = 'mp4',
  onStart,
  onExit,
}) => {
  const renderVideo = () => {
    if (!videoSrc) return null

    if (videoType === 'vimeo') {
      return (
        <VimeoEmbed
          embedCode={videoSrc}
          title={typeof title === 'string' ? title : 'Tutorial Video'}
        />
      )
    }

    return (
      <TutorialVideo
        videoSrc={videoSrc}
        title={typeof title === 'string' ? title : 'Tutorial Video'}
      />
    )
  }

  return (
    <Paper
      elevation={8}
      sx={{
        width: 'clamp(320px, 90vw, 600px)',
        p: { xs: 2, sm: 4 },
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        borderRadius: '12px',
      }}
    >
      {renderVideo()}
      <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mt: 2 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: '450px', fontSize: '14px' }}>
        {content}
      </Typography>
      <Stack spacing={2} sx={{ mt: 2, width: '100%', maxWidth: '240px' }}>
        <Button
          variant="contained"
          size="large"
          onClick={onStart}
          endIcon={<ArrowForwardIcon />}
          sx={{
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#388e3c' },
            fontWeight: 'bold',
          }}
        >
          Start Tour
        </Button>
        <Button variant="text" onClick={onExit} sx={{ color: '#4caf50', fontWeight: 'bold' }}>
          No Thanks, I'll Do It Myself
        </Button>
      </Stack>
    </Paper>
  )
}

export default WelcomeScreen
